// Tag chips and the tag/limit editors shared by the point selection, the
// point list and the point page. Parsing and validation live in
// lib/selection.ts; these components only collect input.
import { useState } from 'react';
import type { BulkPointEditBody, PointEditBody } from './api/types.js';
import {
  formatLimits,
  limitsError,
  parseLimit,
  parseTagList,
  parseTagRemovals,
} from './lib/selection.js';

export function TagChips({
  tags,
  onPick,
}: {
  tags: readonly string[] | undefined;
  /** Clicking a tag filters by it. */
  onPick?: (tag: string) => void;
}) {
  if (!tags?.length) return null;
  return (
    <span className="tag-chips">
      {tags.map((t) =>
        onPick ? (
          <button
            key={t}
            type="button"
            className="tag"
            title={`Filter by ${t}`}
            onClick={() => {
              onPick(t);
            }}
          >
            {t}
          </button>
        ) : (
          <span key={t} className="tag">
            {t}
          </span>
        ),
      )}
    </span>
  );
}

export function Limits({
  min,
  max,
  units,
}: {
  min: number | null | undefined;
  max: number | null | undefined;
  units?: string | null;
}) {
  const text = formatLimits(min, max, units);
  return text ? (
    <span className="limits" title="Limits (guard writes, flag readings)">
      {text}
    </span>
  ) : null;
}

interface LimitFieldsState {
  min: string;
  max: string;
}

function limitsOf(s: LimitFieldsState) {
  const min = parseLimit(s.min);
  const max = parseLimit(s.max);
  if (!min.ok) return { error: `Min: ${min.error}` } as const;
  if (!max.ok) return { error: `Max: ${max.error}` } as const;
  const err = limitsError(min.value, max.value);
  return err ? ({ error: err } as const) : ({ min: min.value, max: max.value } as const);
}

const str = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));

/** Tags (replaced as a whole) and limits of one point. */
export function PointEditor({
  tags,
  min,
  max,
  analog,
  busy,
  onSave,
  onCancel,
}: {
  tags: readonly string[];
  min: number | null;
  max: number | null;
  analog: boolean;
  busy?: boolean;
  onSave: (body: PointEditBody) => void;
  onCancel: () => void;
}) {
  const [tagText, setTagText] = useState(tags.join(', '));
  const [lim, setLim] = useState<LimitFieldsState>({ min: str(min), max: str(max) });
  const parsed = parseTagList(tagText);
  const limits = analog ? limitsOf(lim) : null;
  const errors = [...parsed.errors, ...(limits && 'error' in limits ? [limits.error] : [])];
  return (
    <form
      className="point-editor"
      onSubmit={(e) => {
        e.preventDefault();
        if (errors.length) return;
        const body: PointEditBody = { tags: parsed.tags };
        if (limits && !('error' in limits)) {
          body.min_value = limits.min;
          body.max_value = limits.max;
        }
        onSave(body);
      }}
    >
      <label className="field">
        <span className="small muted">Tags (key=value, comma-separated)</span>
        <input
          type="text"
          value={tagText}
          placeholder="building=E, system=vzt, room=E214"
          className="input-tags"
          aria-label="Tags"
          onChange={(e) => {
            setTagText(e.target.value);
          }}
        />
      </label>
      {analog && (
        <span className="limit-fields">
          <label className="field">
            <span className="small muted">Min</span>
            <input
              type="text"
              inputMode="decimal"
              className="input-limit"
              aria-label="Min value"
              value={lim.min}
              onChange={(e) => {
                setLim({ ...lim, min: e.target.value });
              }}
            />
          </label>
          <label className="field">
            <span className="small muted">Max</span>
            <input
              type="text"
              inputMode="decimal"
              className="input-limit"
              aria-label="Max value"
              value={lim.max}
              onChange={(e) => {
                setLim({ ...lim, max: e.target.value });
              }}
            />
          </label>
        </span>
      )}
      <span className="editor-actions">
        <button
          type="submit"
          className="btn btn-small btn-primary"
          disabled={busy || errors.length > 0}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn-small" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </span>
      {errors.length > 0 && <div className="bad small editor-errors">{errors.join('; ')}</div>}
    </form>
  );
}

/**
 * Tags and limits for many points at once: added tags replace other values
 * of their keys; limits are set only when the box is ticked.
 */
export function BulkPointEditor({
  count,
  analogCount,
  busy,
  onApply,
}: {
  /** Points the edit applies to. */
  count: number;
  /** How many of them are analog (limits apply to these only). */
  analogCount: number;
  busy?: boolean;
  onApply: (body: Omit<BulkPointEditBody, 'point_ids'>) => void;
}) {
  const [add, setAdd] = useState('');
  const [remove, setRemove] = useState('');
  const [setLimits, setSetLimits] = useState(false);
  const [lim, setLim] = useState<LimitFieldsState>({ min: '', max: '' });
  const adds = parseTagList(add);
  const removes = parseTagRemovals(remove);
  const limits = setLimits ? limitsOf(lim) : null;
  const errors = [
    ...adds.errors,
    ...removes.errors,
    ...(limits && 'error' in limits ? [limits.error] : []),
    ...(setLimits && analogCount < count
      ? [`limits apply to analog points only (${String(analogCount)} of ${String(count)})`]
      : []),
  ];
  const empty = adds.tags.length === 0 && removes.tags.length === 0 && !setLimits;
  return (
    <form
      className="bulk-editor"
      onSubmit={(e) => {
        e.preventDefault();
        if (errors.length || empty) return;
        const body: Omit<BulkPointEditBody, 'point_ids'> = {};
        if (adds.tags.length) body.add_tags = adds.tags;
        if (removes.tags.length) body.remove_tags = removes.tags;
        if (limits && !('error' in limits)) {
          body.min_value = limits.min;
          body.max_value = limits.max;
        }
        onApply(body);
      }}
    >
      <input
        type="text"
        className="input-tags"
        aria-label="Tags to add"
        placeholder="Add tags: building=E, system=vzt"
        value={add}
        onChange={(e) => {
          setAdd(e.target.value);
        }}
      />
      <input
        type="text"
        className="input-tags"
        aria-label="Tags to remove"
        placeholder="Remove: room, system=vzt"
        value={remove}
        onChange={(e) => {
          setRemove(e.target.value);
        }}
      />
      <label className="toggle">
        <input
          type="checkbox"
          checked={setLimits}
          onChange={(e) => {
            setSetLimits(e.target.checked);
          }}
        />
        Limits
      </label>
      {setLimits && (
        <>
          <input
            type="text"
            inputMode="decimal"
            className="input-limit"
            aria-label="Min value for all"
            placeholder="min"
            value={lim.min}
            onChange={(e) => {
              setLim({ ...lim, min: e.target.value });
            }}
          />
          <input
            type="text"
            inputMode="decimal"
            className="input-limit"
            aria-label="Max value for all"
            placeholder="max"
            value={lim.max}
            onChange={(e) => {
              setLim({ ...lim, max: e.target.value });
            }}
          />
        </>
      )}
      <button
        type="submit"
        className="btn btn-small"
        disabled={busy || empty || errors.length > 0 || count === 0}
        title="Tags and limits apply right away (no Save needed)"
      >
        {busy ? 'Applying…' : `Apply to ${String(count)} point${count === 1 ? '' : 's'}`}
      </button>
      {errors.length > 0 && <div className="bad small editor-errors">{errors.join('; ')}</div>}
    </form>
  );
}
